"use client";
import { useState, useCallback } from "react";
import { ProductSummary } from "@/common/productSummary/types";
import { buildFacetFiltersFromQuiz } from "@/lib/productSummary/buildFacetFiltersFromQuiz";
import { QuizConfig } from "../type";
import clientSearch from "@/lib/algolia/clientSearch";
import { PRODUCT_SUMMARY_INDEX_EN } from "@/constants/algolia";
import getFilters from "@/components/Search/utils/getFilters";

export interface UseQuizSearchProps {
  searchableKeys: Set<string>;
  quizConfig: QuizConfig;
  hitsPerPage?: number;
  language: string;
}

export const useQuizSearch = ({
  searchableKeys,
  quizConfig,
  hitsPerPage = 4,
  language,
}: UseQuizSearchProps) => {
  const [results, setResults] = useState<ProductSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const executeSearch = useCallback(
    async (answers: Record<string, string | string[]>) => {
      setIsLoading(true);
      setError(null);

      try {
        const facetFilters = buildFacetFiltersFromQuiz(
          answers,
          searchableKeys,
          quizConfig,
        );

        const searchResponse = await clientSearch([
          {
            indexName: PRODUCT_SUMMARY_INDEX_EN,
            params: {
              facetFilters,
              optionalFilters: [`language:${language}`],
              hitsPerPage,
            },
          },
        ]);

        const hits = (searchResponse.results[0]?.hits ||
          []) as ProductSummary[];
        setResults(hits);
        return hits;
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Search failed");
        console.error("Error processing quiz results:", error);
        setError(error);
        setResults([]);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [searchableKeys, quizConfig, hitsPerPage, language],
  );

  const hydrateResults = useCallback((persisted: ProductSummary[]) => {
    setResults(persisted);
    setIsLoading(false);
    setError(null);
  }, []);

  const reset = useCallback(() => {
    hydrateResults([]);
  }, [hydrateResults]);

  return {
    results,
    isLoading,
    error,
    executeSearch,
    hydrateResults,
    reset,
  };
};
